import { useState } from 'react';

interface MaterialPaletteProps {
  darkMode: boolean;
  selectedStyle: string;
  onMaterialChange: (materials: MaterialSelection) => void;
  currentMaterials: MaterialSelection;
}

export interface MaterialSelection {
  wallColor: string;
  wallMaterial: string;
  floorMaterial: string;
  roofMaterial: string;
  accentColor: string;
}

const WALL_COLORS = [
  { id: 'white', label: 'Crisp White', hex: '#F8FAFC', tw: 'bg-slate-50' },
  { id: 'cream', label: 'Warm Cream', hex: '#FEF9EF', tw: 'bg-amber-50' },
  { id: 'gray', label: 'Cool Gray', hex: '#E2E8F0', tw: 'bg-slate-200' },
  { id: 'beige', label: 'Sand Beige', hex: '#F5F0E8', tw: 'bg-stone-100' },
  { id: 'charcoal', label: 'Charcoal', hex: '#374151', tw: 'bg-gray-700' },
  { id: 'navy', label: 'Deep Navy', hex: '#1E3A5F', tw: 'bg-blue-900' },
  { id: 'sage', label: 'Sage Green', hex: '#84A98C', tw: 'bg-green-400' },
  { id: 'terracotta', label: 'Terracotta', hex: '#C4704F', tw: 'bg-orange-500' },
  { id: 'blush', label: 'Blush Rose', hex: '#E8C4B8', tw: 'bg-rose-200' },
  { id: 'slate', label: 'Slate Blue', hex: '#64748B', tw: 'bg-slate-500' },
];

const WALL_MATERIALS = [
  { id: 'stucco', label: 'Stucco', icon: 'ri-contrast-drop-line', desc: 'Smooth plaster finish' },
  { id: 'brick', label: 'Brick', icon: 'ri-layout-grid-fill', desc: 'Classic fired clay' },
  { id: 'wood', label: 'Wood Siding', icon: 'ri-align-left', desc: 'Horizontal planks' },
  { id: 'stone', label: 'Natural Stone', icon: 'ri-landscape-line', desc: 'Quarried stone' },
  { id: 'concrete', label: 'Concrete', icon: 'ri-building-line', desc: 'Exposed raw finish' },
  { id: 'glass', label: 'Glass Curtain', icon: 'ri-window-line', desc: 'Floor-to-ceiling glass' },
  { id: 'metal', label: 'Metal Panel', icon: 'ri-grid-line', desc: 'Corrugated steel' },
  { id: 'vinyl', label: 'Vinyl Siding', icon: 'ri-align-justify', desc: 'Low-maintenance panels' },
];

const FLOOR_MATERIALS = [
  { id: 'hardwood', label: 'Hardwood', icon: 'ri-align-left', desc: 'Oak or walnut planks', color: '#8B6914' },
  { id: 'marble', label: 'Marble', icon: 'ri-contrast-2-line', desc: 'Polished stone', color: '#E8E8E8' },
  { id: 'tile', label: 'Ceramic Tile', icon: 'ri-layout-grid-line', desc: 'Porcelain or ceramic', color: '#CBD5E1' },
  { id: 'concrete', label: 'Polished Concrete', icon: 'ri-building-line', desc: 'Industrial finish', color: '#94A3B8' },
  { id: 'bamboo', label: 'Bamboo', icon: 'ri-plant-line', desc: 'Eco-friendly option', color: '#A3B18A' },
  { id: 'carpet', label: 'Carpet', icon: 'ri-artboard-line', desc: 'Soft textile', color: '#C4A882' },
  { id: 'vinyl', label: 'Luxury Vinyl', icon: 'ri-stack-line', desc: 'Waterproof planks', color: '#B8A99A' },
  { id: 'terrazzo', label: 'Terrazzo', icon: 'ri-bubble-chart-line', desc: 'Speckled composite', color: '#D4C5B0' },
];

const ROOF_MATERIALS = [
  { id: 'asphalt', label: 'Asphalt Shingle', icon: 'ri-home-line', desc: 'Most common, affordable' },
  { id: 'metal', label: 'Metal Roof', icon: 'ri-building-2-line', desc: 'Durable, modern look' },
  { id: 'terracotta', label: 'Terracotta Tile', icon: 'ri-sun-line', desc: 'Mediterranean style' },
  { id: 'slate', label: 'Slate Tile', icon: 'ri-landscape-line', desc: 'Premium natural stone' },
  { id: 'flat', label: 'Flat / TPO', icon: 'ri-subtract-line', desc: 'Modern flat roof' },
  { id: 'green', label: 'Green Roof', icon: 'ri-plant-line', desc: 'Living vegetation' },
  { id: 'cedar', label: 'Cedar Shake', icon: 'ri-align-left', desc: 'Rustic wood shingles' },
  { id: 'concrete', label: 'Concrete Tile', icon: 'ri-grid-line', desc: 'Heavy, durable' },
];

const ACCENT_COLORS = [
  { id: 'black', label: 'Matte Black', hex: '#1C1C1E' },
  { id: 'bronze', label: 'Bronze', hex: '#8B6914' },
  { id: 'white', label: 'Pure White', hex: '#FFFFFF' },
  { id: 'teal', label: 'Teal', hex: '#0D9488' },
  { id: 'copper', label: 'Copper', hex: '#B87333' },
  { id: 'navy', label: 'Navy', hex: '#1E3A5F' },
  { id: 'gold', label: 'Brushed Gold', hex: '#C9A84C' },
  { id: 'charcoal', label: 'Charcoal', hex: '#374151' },
];

// Style-based material presets
const STYLE_PRESETS: Record<string, MaterialSelection> = {
  Minimalist: { wallColor: '#F8FAFC', wallMaterial: 'concrete', floorMaterial: 'concrete', roofMaterial: 'flat', accentColor: '#1C1C1E' },
  Farmhouse: { wallColor: '#F8FAFC', wallMaterial: 'wood', floorMaterial: 'hardwood', roofMaterial: 'metal', accentColor: '#1C1C1E' },
  Mediterranean: { wallColor: '#FEF9EF', wallMaterial: 'stucco', floorMaterial: 'tile', roofMaterial: 'terracotta', accentColor: '#8B6914' },
  Victorian: { wallColor: '#FEF9EF', wallMaterial: 'wood', floorMaterial: 'hardwood', roofMaterial: 'slate', accentColor: '#8B6914' },
  Colonial: { wallColor: '#F8FAFC', wallMaterial: 'brick', floorMaterial: 'hardwood', roofMaterial: 'asphalt', accentColor: '#1C1C1E' },
  Craftsman: { wallColor: '#F5F0E8', wallMaterial: 'wood', floorMaterial: 'hardwood', roofMaterial: 'cedar', accentColor: '#8B6914' },
  Contemporary: { wallColor: '#E2E8F0', wallMaterial: 'glass', floorMaterial: 'concrete', roofMaterial: 'flat', accentColor: '#374151' },
  Industrial: { wallColor: '#374151', wallMaterial: 'concrete', floorMaterial: 'concrete', roofMaterial: 'metal', accentColor: '#B87333' },
  Scandinavian: { wallColor: '#F8FAFC', wallMaterial: 'wood', floorMaterial: 'hardwood', roofMaterial: 'metal', accentColor: '#1C1C1E' },
  Tudor: { wallColor: '#F5F0E8', wallMaterial: 'stone', floorMaterial: 'hardwood', roofMaterial: 'slate', accentColor: '#374151' },
  'A-Frame': { wallColor: '#F5F0E8', wallMaterial: 'wood', floorMaterial: 'hardwood', roofMaterial: 'cedar', accentColor: '#8B6914' },
  Ranch: { wallColor: '#F5F0E8', wallMaterial: 'brick', floorMaterial: 'hardwood', roofMaterial: 'asphalt', accentColor: '#374151' },
};

const DEFAULT_MATERIALS: MaterialSelection = {
  wallColor: '#F8FAFC',
  wallMaterial: 'stucco',
  floorMaterial: 'hardwood',
  roofMaterial: 'asphalt',
  accentColor: '#1C1C1E',
};

export { DEFAULT_MATERIALS };

export default function MaterialPalette({ darkMode, selectedStyle, onMaterialChange, currentMaterials }: MaterialPaletteProps) {
  const [activeSection, setActiveSection] = useState<'wall' | 'floor' | 'roof' | 'accent'>('wall');
  const [showPresetApplied, setShowPresetApplied] = useState(false);

  const dm = darkMode;
  const text = dm ? 'text-white' : 'text-gray-900';
  const subtext = dm ? 'text-gray-400' : 'text-gray-500';
  const cardBg = dm ? 'bg-gray-700/60 border-gray-600' : 'bg-white border-gray-200';
  const sectionBg = dm ? 'bg-gray-800' : 'bg-gray-50';

  const applyStylePreset = () => {
    const preset = STYLE_PRESETS[selectedStyle] || DEFAULT_MATERIALS;
    onMaterialChange(preset);
    setShowPresetApplied(true);
    setTimeout(() => setShowPresetApplied(false), 2000);
  };

  const sections = [
    { id: 'wall' as const, label: 'Walls', icon: 'ri-building-line' },
    { id: 'floor' as const, label: 'Floors', icon: 'ri-layout-bottom-line' },
    { id: 'roof' as const, label: 'Roof', icon: 'ri-home-line' },
    { id: 'accent' as const, label: 'Accents', icon: 'ri-palette-line' },
  ];

  return (
    <div className={`rounded-2xl border overflow-hidden ${dm ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${dm ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-pink-500">
            <i className="ri-palette-line text-white text-sm"></i>
          </div>
          <div>
            <p className={`text-sm font-bold ${text}`}>Material Palette</p>
            <p className={`text-xs ${subtext}`}>Customize finishes &amp; colors</p>
          </div>
        </div>
        {selectedStyle && (
          <button
            onClick={applyStylePreset}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              showPresetApplied
                ? 'bg-teal-500 text-white'
                : dm ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
            }`}
          >
            {showPresetApplied ? (
              <><i className="ri-check-line"></i>Applied!</>
            ) : (
              <><i className="ri-magic-line"></i>{selectedStyle} Preset</>
            )}
          </button>
        )}
      </div>

      {/* Current selection preview strip */}
      <div className="flex h-8">
        <div className="flex-1" style={{ backgroundColor: currentMaterials.wallColor }} title="Wall Color" />
        <div className="flex-1" style={{ backgroundColor: FLOOR_MATERIALS.find(f => f.id === currentMaterials.floorMaterial)?.color || '#8B6914' }} title="Floor" />
        <div className="w-8" style={{ backgroundColor: currentMaterials.accentColor }} title="Accent" />
      </div>

      {/* Section tabs */}
      <div className={`flex border-b ${dm ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-all cursor-pointer ${
              activeSection === s.id
                ? dm ? 'text-teal-400 border-b-2 border-teal-400 bg-gray-700/50' : 'text-teal-600 border-b-2 border-teal-500 bg-white'
                : dm ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={s.icon}></i>
            {s.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* WALL section */}
        {activeSection === 'wall' && (
          <div className="space-y-4">
            <div>
              <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${subtext}`}>Wall Color</p>
              <div className="grid grid-cols-5 gap-2">
                {WALL_COLORS.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => onMaterialChange({ ...currentMaterials, wallColor: c.hex })}
                    title={c.label}
                    className={`relative group flex flex-col items-center gap-1 cursor-pointer`}
                  >
                    <div
                      className={`w-full h-8 rounded-lg border-2 transition-all ${
                        currentMaterials.wallColor === c.hex
                          ? 'border-teal-500 scale-110 shadow-md'
                          : dm ? 'border-gray-600 hover:border-gray-400' : 'border-gray-200 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: c.hex }}
                    />
                    <span className={`text-xs leading-tight text-center ${subtext}`} style={{ fontSize: '9px' }}>{c.label}</span>
                    {currentMaterials.wallColor === c.hex && (
                      <div className="absolute top-1 right-1 w-3 h-3 bg-teal-500 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white" style={{ fontSize: '8px' }}></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${subtext}`}>Wall Material</p>
              <div className="grid grid-cols-2 gap-2">
                {WALL_MATERIALS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => onMaterialChange({ ...currentMaterials, wallMaterial: m.id })}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      currentMaterials.wallMaterial === m.id
                        ? dm ? 'border-teal-500 bg-teal-900/30' : 'border-teal-500 bg-teal-50'
                        : dm ? 'border-gray-600 bg-gray-700/40 hover:border-gray-500' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${m.icon} text-sm ${currentMaterials.wallMaterial === m.id ? 'text-teal-500' : subtext}`}></i>
                    <div>
                      <p className={`text-xs font-bold ${currentMaterials.wallMaterial === m.id ? (dm ? 'text-teal-300' : 'text-teal-700') : text}`}>{m.label}</p>
                      <p className={`text-xs ${subtext}`} style={{ fontSize: '9px' }}>{m.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FLOOR section */}
        {activeSection === 'floor' && (
          <div>
            <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${subtext}`}>Flooring Material</p>
            <div className="grid grid-cols-2 gap-2">
              {FLOOR_MATERIALS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => onMaterialChange({ ...currentMaterials, floorMaterial: m.id })}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                    currentMaterials.floorMaterial === m.id
                      ? dm ? 'border-teal-500 bg-teal-900/30' : 'border-teal-500 bg-teal-50'
                      : dm ? 'border-gray-600 bg-gray-700/40 hover:border-gray-500' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg flex-shrink-0 border border-gray-300" style={{ backgroundColor: m.color }} />
                  <div>
                    <p className={`text-xs font-bold ${currentMaterials.floorMaterial === m.id ? (dm ? 'text-teal-300' : 'text-teal-700') : text}`}>{m.label}</p>
                    <p className={`text-xs ${subtext}`} style={{ fontSize: '9px' }}>{m.desc}</p>
                  </div>
                  {currentMaterials.floorMaterial === m.id && (
                    <i className="ri-check-line text-teal-500 ml-auto text-sm"></i>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ROOF section */}
        {activeSection === 'roof' && (
          <div>
            <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${subtext}`}>Roof Material</p>
            <div className="grid grid-cols-2 gap-2">
              {ROOF_MATERIALS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => onMaterialChange({ ...currentMaterials, roofMaterial: m.id })}
                  className={`flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all cursor-pointer ${
                    currentMaterials.roofMaterial === m.id
                      ? dm ? 'border-teal-500 bg-teal-900/30' : 'border-teal-500 bg-teal-50'
                      : dm ? 'border-gray-600 bg-gray-700/40 hover:border-gray-500' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <i className={`${m.icon} text-sm ${currentMaterials.roofMaterial === m.id ? 'text-teal-500' : subtext}`}></i>
                  <div>
                    <p className={`text-xs font-bold ${currentMaterials.roofMaterial === m.id ? (dm ? 'text-teal-300' : 'text-teal-700') : text}`}>{m.label}</p>
                    <p className={`text-xs ${subtext}`} style={{ fontSize: '9px' }}>{m.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ACCENT section */}
        {activeSection === 'accent' && (
          <div>
            <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${subtext}`}>Accent &amp; Trim Color</p>
            <div className="grid grid-cols-4 gap-3">
              {ACCENT_COLORS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onMaterialChange({ ...currentMaterials, accentColor: c.hex })}
                  title={c.label}
                  className="flex flex-col items-center gap-1.5 cursor-pointer group"
                >
                  <div
                    className={`w-12 h-12 rounded-xl border-2 transition-all ${
                      currentMaterials.accentColor === c.hex
                        ? 'border-teal-500 scale-110 shadow-md'
                        : dm ? 'border-gray-600 hover:border-gray-400' : 'border-gray-200 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: c.hex }}
                  />
                  <span className={`text-xs text-center leading-tight ${subtext}`} style={{ fontSize: '9px' }}>{c.label}</span>
                </button>
              ))}
            </div>

            {/* Current combo preview */}
            <div className={`mt-4 p-3 rounded-xl border ${dm ? 'bg-gray-700/40 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-xs font-semibold mb-2 ${subtext}`}>Current Combination</p>
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-8 h-8 rounded-lg border border-gray-300" style={{ backgroundColor: currentMaterials.wallColor }} title="Wall" />
                  <div className="w-8 h-8 rounded-lg border border-gray-300" style={{ backgroundColor: FLOOR_MATERIALS.find(f => f.id === currentMaterials.floorMaterial)?.color || '#8B6914' }} title="Floor" />
                  <div className="w-8 h-8 rounded-lg border border-gray-300" style={{ backgroundColor: currentMaterials.accentColor }} title="Accent" />
                </div>
                <div className={`text-xs ${subtext}`}>
                  <p>{WALL_MATERIALS.find(m => m.id === currentMaterials.wallMaterial)?.label} walls</p>
                  <p>{FLOOR_MATERIALS.find(f => f.id === currentMaterials.floorMaterial)?.label} floors</p>
                  <p>{ROOF_MATERIALS.find(r => r.id === currentMaterials.roofMaterial)?.label} roof</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
